import type { Partner } from "@expert/shared-types";

import { getLogger } from "@expert/logging";

import type { AgentActivity, ChatTask } from "@/sdk";
import type { AsyncState } from "@/shared-types/utility";

import { useChatStore } from "@/sdk/agent/chat";
import { DEFAULT_CHAT_WRAPPING_DURATION } from "@/sdk/agent/config";

import type { TwilioChatTaskAttributes } from "../twilioTaskAttributes";

import { TwilioTask, type TwilioTaskConfig, type TwilioTaskProps } from "../twilioTask";

const logger = getLogger({
    module: "TwilioChatTask",
});

function getTaskPartner(config: TwilioTaskConfig) {
    const partner = config.reservation.task.attributes.partner as Partner;
    return partner.toLowerCase();
}

export class TwilioChatTask extends TwilioTask<TwilioChatTaskAttributes> implements ChatTask {
    public override readonly onAcceptStatus = "Available" as AgentActivity;
    public override readonly hasCustomer = true;
    public override readonly sessionId = undefined;
    public override readonly partner: Partner;
    public override readonly autoAccept = false;
    public override readonly wrappingDuration: number = DEFAULT_CHAT_WRAPPING_DURATION;

    constructor(name: string, props: TwilioTaskProps, agentPartner: Partner) {
        super(name, props);
        this.partner = getTaskPartner(this.config) ?? agentPartner;
        this.mdn = { loading: false };
        this.problemStatement = { loading: false };
        this.customerName = { loading: false };
    }

    get chatId(): string {
        return this.attributes.conversationSid;
    }

    public mdn: AsyncState<string | undefined>;
    public problemStatement: AsyncState<string | undefined>;
    public customerName: AsyncState<string | undefined>;

    public get isCustomerAbsent(): boolean {
        const autopilotState = useChatStore.getState().getChatAutopilot(this.chatId);
        return !!autopilotState?.startTime;
    }

    public get customerAbsenceStartedAt(): number | undefined {
        return useChatStore.getState().getChatAutopilot(this.chatId)?.startTime;
    }

    public get customerAbsenceEndsAt(): number | undefined {
        const autopilotState = useChatStore.getState().getChatAutopilot(this.chatId);
        return autopilotState?.startTime && autopilotState?.durationSeconds !== undefined
            ? autopilotState.startTime + autopilotState.durationSeconds * 1000
            : undefined;
    }

    public get isNeglected(): boolean {
        const neglectionState = useChatStore.getState().getChatNeglection(this.chatId);
        return !!neglectionState?.warningCount;
    }

    public get wasTaskNeglected(): boolean {
        return this.attributes?.transferReason === "Neglection";
    }

    public get isClosedByCustomer(): boolean {
        return this.statusReason === "ClosedByCustomer";
    }

    public get wasCustomerDisconnected(): boolean {
        return this.statusReason === "CustomerDisconnected";
    }

    public get changeStateAt(): number | undefined {
        if (this.status === "wrapping") {
            const wrappingStart = this.statusUpdatedAt?.getTime() ?? this.config.reservation.task.dateUpdated.getTime();
            return wrappingStart + this.wrappingDuration;
        }

        if (this.isNeglected) {
            const neglectionState = useChatStore.getState().getChatNeglection(this.chatId);
            return neglectionState?.startTime && this.neglectionWarningDurationMillis !== undefined
                ? neglectionState.startTime + this.neglectionWarningDurationMillis
                : undefined;
        }

        if (this.isCustomerAbsent && this.customerAbsenceEndsAt) {
            return this.customerAbsenceEndsAt;
        }

        return undefined;
    }

    public get stateLastChangedAt(): number | undefined {
        if (this.status === "wrapping") {
            return (this.statusUpdatedAt ?? this.config.reservation.task.dateUpdated).getTime();
        }

        if (this.isNeglected) {
            return useChatStore.getState().getChatNeglection(this.chatId)?.startTime;
        }

        if (this.isCustomerAbsent && this.customerAbsenceStartedAt) {
            return this.customerAbsenceStartedAt;
        }

        return undefined;
    }

    public override get previousTaskId(): string | undefined {
        throw new Error("previousTaskID is not supported for Twilio Chat");
    }

    public override onAccepted(): void | Promise<void> {} // not used

    public override isAgentBusy(_agentActivity: AgentActivity): boolean {
        return ["assigned", "wrapping", "pending"].includes(this.status);
    }

    /**
     * Get the total neglection warning duration in milliseconds.
     * This is the total time from the first warning until task transfer.
     * The timer uses startTime + neglectionWarningDurationMillis to calculate end time.
     *
     * @returns The total duration in milliseconds, or undefined if not in neglection state.
     */
    get neglectionWarningDurationMillis(): number | undefined {
        const neglectionState = useChatStore.getState().getChatNeglection(this.chatId);
        if (!neglectionState?.warningCount) return undefined;
        return neglectionState.durationSeconds ? neglectionState.durationSeconds * 1000 : undefined;
    }

    public override get analyticsData(): Record<string, string> {
        return {
            ...super.analyticsData,
            ChatId: this.chatId,
        };
    }

    public get routingQueue(): string | undefined {
        return this.config.reservation.task.queueName;
    }

    public get sourceApp(): string | undefined {
        const sourceApp = this.attributes.sourceApp;
        if (!sourceApp) {
            logger.error(
                {
                    chatId: this.chatId,
                    taskSid: this.config.reservation.task.sid,
                },
                "CRITICAL: sourceApp is missing from chat task attributes",
            );
        }
        return sourceApp;
    }

    public get workerSid(): string {
        return this.worker.sid;
    }

    public get topic(): string | undefined {
        const topicFromAttributes = this.config.reservation.task.attributes.topic as string | undefined;

        if (!topicFromAttributes) {
            logger.error(
                {
                    chatId: this.chatId,
                    taskSid: this.config.reservation.task.sid,
                },
                "CRITICAL: Topic is missing from task attributes - sales features will not work",
            );
        }

        return topicFromAttributes;
    }

    public get language(): string | undefined {
        const languageFromAttributes = this.config.reservation.task.attributes.language as string | undefined;

        if (!languageFromAttributes) {
            logger.error(
                {
                    chatId: this.chatId,
                    taskSid: this.config.reservation.task.sid,
                },
                "CRITICAL: Language is missing from task attributes - sales features will not work",
            );
        }

        return languageFromAttributes;
    }

    public get neglectionCount(): number | undefined {
        return this.attributes.ignoreExpertIds?.length;
    }

    override toLog(): Record<string, unknown> {
        return {
            ...super.toLog(),
            chatId: this.chatId,
        };
    }
}
